/**
 * useNavigationContexts.ts - React hook for managing navigation contexts
 * 
 * CHANGE HISTORY:
 * - 251122_1517 danielbeer: Created for Phase 5 integration
 */

import { useState, useEffect } from 'react';
import type { NavigationContext } from '../components/ContextSelector';
import { getOrganizrContextStorage } from '../storage/OrganizrContextStorage';
import { getTemplatrContextStorage } from '../storage/TemplatrContextStorage';

export interface UseNavigationContextsResult {
  fixedContexts: NavigationContext[];
  userContexts: NavigationContext[];
  dynamicContext: NavigationContext | null;
  currentContext: NavigationContext | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setCurrentContext: (context: NavigationContext) => void;
  createUserContext: (context: Omit<NavigationContext, 'id' | 'type'>) => Promise<void>;
  updateUserContext: (contextId: string, updates: Partial<NavigationContext>) => Promise<void>;
  deleteUserContext: (contextId: string) => Promise<void>;
  setDynamicContext: (context: NavigationContext | null) => void;
  refresh: () => Promise<void>;
}

export function useNavigationContexts(): UseNavigationContextsResult {
  const [fixedContexts, setFixedContexts] = useState<NavigationContext[]>([]);
  const [userContexts, setUserContexts] = useState<NavigationContext[]>([]);
  const [dynamicContext, setDynamicContext] = useState<NavigationContext | null>(null);
  const [currentContext, setCurrentContext] = useState<NavigationContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load contexts
  const loadContexts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const organizrStorage = getOrganizrContextStorage();
      const templatrStorage = getTemplatrContextStorage();

      const [fixed, user] = await Promise.all([
        organizrStorage.getFixedContexts(),
        templatrStorage.getUserContexts()
      ]);

      setFixedContexts(fixed);
      setUserContexts(user);

      // Set default context if none selected
      if (!currentContext && fixed.length > 0) {
        setCurrentContext(fixed[0]);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadContexts();
  }, []);

  // Listen for FileMaker context updates
  useEffect(() => {
    const handleFMContext = (event: CustomEvent) => {
      const context = event.detail.context as NavigationContext;
      setDynamicContext(context);
      setCurrentContext(context);
    };

    window.addEventListener('fm:navigation-context' as any, handleFMContext);
    return () => window.removeEventListener('fm:navigation-context' as any, handleFMContext);
  }, []);

  // Create user context
  const createUserContext = async (contextData: Omit<NavigationContext, 'id' | 'type'>) => {
    const templatrStorage = getTemplatrContextStorage();
    const newContext: NavigationContext = {
      ...contextData,
      id: `user-${Date.now()}`,
      type: 'user'
    };

    await templatrStorage.saveUserContext(newContext);
    await loadContexts();
  };

  // Update user context
  const updateUserContext = async (contextId: string, updates: Partial<NavigationContext>) => {
    const templatrStorage = getTemplatrContextStorage();
    await templatrStorage.updateUserContext(contextId, updates);
    await loadContexts();
  };

  // Delete user context
  const deleteUserContext = async (contextId: string) => {
    const templatrStorage = getTemplatrContextStorage();
    await templatrStorage.deleteUserContext(contextId);
    await loadContexts();
  };

  return {
    fixedContexts,
    userContexts,
    dynamicContext,
    currentContext,
    isLoading,
    error,
    setCurrentContext,
    createUserContext,
    updateUserContext,
    deleteUserContext,
    setDynamicContext,
    refresh: loadContexts
  };
}
